import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, ViewChild, OnChanges, SimpleChanges } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { TooltipModule } from 'primeng/tooltip';
import { IContract, IProjectDocument } from '../../../contract-management/models/contract-management.model';
import { IUser, SHARE_ACCESS, IStatus } from '../../../@core/models/model';
import { IContractResponse } from '../../../contract-management/services/contracts.service';
import { SanitizeHtmlPipe } from '../../pipes/sanitizeHtml.pipe';
import { SVG } from '../../../common/svgs';
import { AvatarStackComponent } from '../avatar-stack/avatar-stack.component';
import { DateFormatterPipe } from '../../pipes/dateFormater.pipe';
import { USER_ROLE } from 'src/app/@core/models/enums';
import { TextCasePipe } from "../../pipes/textCase.pipe";
import { MenuComponent, CustomMenuItem } from '../../../@core/components/ui/menu/menu.component';
import { IconComponent } from 'src/app/@core/components/ui/icon/icon.component';

@Component({
    selector: 'app-document-card',
    imports: [
        CommonModule,
        TooltipModule,
        SanitizeHtmlPipe,
        AvatarStackComponent,
        DateFormatterPipe,
        TextCasePipe,
        MenuComponent,
        IconComponent
    ],
    standalone: true,
    templateUrl: './document-card.component.html',
    styleUrl: './document-card.component.scss'
})
export class DocumentCardComponent implements OnChanges {
    @Input() document!: IContractResponse | IProjectDocument;
    @Input() currentUser!: IUser;
    @Input() contractMenuItems: MenuItem[] | undefined = [];
    @Input() showGenerationLoader = false;
    @Input() showProjectName: boolean = false;
    @Input() showDocumentType = true;

    @Input() permissions: {
        canEdit: boolean,
        canComment: boolean,
        canView: boolean,
        canDelete: boolean,
        canDownload: boolean
    } = {
            canEdit: false,
            canComment: false,
            canView: false,
            canDelete: false,
            canDownload: false
        };

    @Output() documentClick = new EventEmitter<string>();
    @Output() menuClick = new EventEmitter<{ event: Event, documentId: string, documentName: string, item: MenuItem }>();
    @Output() popoverToggle = new EventEmitter<{ documentId: string, isOpen: boolean }>();

    SHARE_ACCESS = SHARE_ACCESS;
    USER_ROLE = USER_ROLE;
    SVG = SVG;

    @ViewChild('menu') menu!: MenuComponent;

    menuOptions: CustomMenuItem[] = [];

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['contractMenuItems']) {
            this.updateMenuOptions();
        }
    }

    private updateMenuOptions(): void {
        if (this.contractMenuItems && this.contractMenuItems[0]?.items) {
            this.menuOptions = this.contractMenuItems[0].items.map(item => ({
                label: item.label || '',
                icon: item.icon,
                value: item.label || '',
                callback: () => {
                    const documentName = this.document.name || '';
                    this.menuClick.emit({
                        event: new Event('click'),
                        documentId: this.document._id,
                        documentName,
                        item
                    });
                },
                class: item.styleClass
            }));
        } else {
            this.menuOptions = [];
        }
    }

    openContract(documentId: string) {
        this.documentClick.emit(documentId);
    }

    handleMenuSelection(customItem: CustomMenuItem): void {
        // Find the original MenuItem from contractMenuItems
        const originalItem = this.contractMenuItems?.[0]?.items?.find(
            item => item.label === customItem.label
        );

        if (originalItem) {
            const documentName = this.document.name || '';
            this.menuClick.emit({
                event: new Event('click'),
                documentId: this.document._id,
                documentName,
                item: originalItem
            });
        }
    }

    onPopoverToggle(isVisible: boolean) {
        // Emit the toggle event to parent with document ID
        this.popoverToggle.emit({
            documentId: this.document._id,
            isOpen: isVisible
        });
    }

    closePopover() {
        if (this.menu) {
            this.menu.hide();
        }
    }

    getDocumentStatusName(contract: IContract): string {
        return (contract?.documentStatus as IStatus)?.name || '';
    }

    get isPinned(): boolean {
        return (this.document as IProjectDocument)?.isPinned || false;
    }

    get dueDateExceeding(): boolean {
        return (this.document as IContractResponse)?.dueDateExceeding || false;
    }
}